import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAtom, useAtomValue } from 'jotai';
import { Download, Replace, Upload } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  EDIT_IMAGE_METHOD,
  magicToolOptions,
} from '@/features/image-rendering/constants/toggle-group-options';
import { cn } from '@/lib/utils';

import { selectedImageAtom, selectedPreviewImageAtom } from '../atoms/imageFiles';
import { editMethodAtom, magicToolOptionAtom } from '../atoms/settings';

interface ImageRenderingToolbarProps {
  onDownload: () => void;
  onExport: () => void;
}

export const ImageRenderingToolbar = ({ onDownload, onExport }: ImageRenderingToolbarProps) => {
  const selectedPreviewImage = useAtomValue(selectedPreviewImageAtom);
  const [selectedImage, setSelectedImage] = useAtom(selectedImageAtom);
  const editMethod = useAtomValue(editMethodAtom);
  const { t } = useTranslation();
  const [magicToolOption, setMagicToolOption] = useAtom(magicToolOptionAtom);

  const generalToolOptions = useMemo(() => {
    return [
      {
        icon: Download,
        label: t('download'),
        onClick: onDownload,
      },
      {
        icon: Upload,
        label: t('Export'),
        onClick: onExport,
      },
      ...(selectedImage?.id !== selectedPreviewImage?.id
        ? [
            {
              icon: Replace,
              label: t('select-as-base'),
              onClick: () => {
                if (selectedPreviewImage?.renderedImage) {
                  const updatedImage = {
                    ...selectedPreviewImage,
                    originalImage: selectedPreviewImage.renderedImage,
                  };
                  updatedImage.renderedImage = undefined;

                  setSelectedImage(updatedImage);
                } else {
                  setSelectedImage(selectedPreviewImage);
                }
              },
            },
          ]
        : []),
    ];
  }, [selectedImage, selectedPreviewImage, setSelectedImage, onExport, onDownload, t]);

  return (
    <div className="flex items-center">
      {editMethod === EDIT_IMAGE_METHOD.MAGIC ? (
        <ToggleGroup
          className="gap-2 rounded-sm bg-text-primary px-3 py-2 shadow-lg shadow-black/10"
          type="single"
          disabled={!selectedPreviewImage?.renderedImage}
          value={magicToolOption ? (magicToolOption as string) : ''}
          onValueChange={setMagicToolOption as (value: string) => void}
        >
          {magicToolOptions.map((item, index) => (
            <ToggleGroupItem
              key={`edit-image-method-toggle-item-${String.fromCharCode(65 + index)}`}
              className={cn(
                'h-[24px] rounded-sm px-1',
                magicToolOption === item.value ? '!bg-[#2C414B]' : '!bg-transparent'
              )}
              value={item.value}
            >
              <item.icon
                className={cn(
                  '!h-[18px] !w-[18px]',
                  magicToolOption === item.value ? 'text-primary-light' : 'text-white'
                )}
              />
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      ) : (
        <div className="flex flex-col gap-2">
          {generalToolOptions.map((item, index) => (
            <Button
              key={`edit-image-method-toggle-item-${String.fromCharCode(65 + index)}`}
              className="my-auto min-w-12 rounded-sm bg-text-primary px-1 hover:!bg-[#2C414B]"
              disabled={!selectedPreviewImage}
              onClick={item.onClick}
            >
              {item.label ? <span className="mr-2 hidden sm:block">{item.label}</span> : null}
              <item.icon className="!h-[18px] !w-[18px] text-white" />
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};
